/** Create / edit dialog for a preflight item template (react-hook-form + zod).
 *
 *  On CREATE the `action` is picked from the backend action catalog; picking an
 *  action prefills title / task_type / owner_role / optional / steps from that
 *  action's registry defaults. On EDIT the action is immutable (it keys the
 *  template) and shown read-only. A `useFieldArray` steps editor lets multistep
 *  templates declare an ordered list of {title, description} rows. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  getActionCatalog,
  groups,
  templates,
  type PreflightActionDef,
  type PreflightOwnerRole,
  type PreflightTaskType,
  type PreflightTemplate,
  type PreflightTemplateWrite,
} from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

const TASK_TYPE_LABELS: Record<PreflightTaskType, string> = {
  manual: 'Manual',
  async: 'Async',
  multistep: 'Multistep',
};

const OWNER_ROLE_LABELS: Record<Exclude<PreflightOwnerRole, ''>, string> = {
  ops: 'Ops',
  messaging: 'Messaging',
  infra: 'Infra',
  app_stores: 'App stores',
  det: 'Data Entry Team',
  dev: 'Development',
};

const StepSchema = z.object({
  title: z.string().min(1, 'Required'),
  description: z.string(),
});

const FormSchema = z.object({
  action: z.string().min(1, 'Required'),
  group: z.string(), // '' = ungrouped; else the group id as a string (Select value)
  task_type: z.enum(['manual', 'async', 'multistep']),
  owner_role: z.string(), // '' allowed (unassigned)
  title: z.string().min(1, 'Required'),
  optional: z.boolean(),
  active: z.boolean(),
  sequence: z.number({ invalid_type_error: 'Required' }).int('Whole number').min(0, 'Must be ≥ 0'),
  steps: z.array(StepSchema),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  action: '',
  group: '',
  task_type: 'manual',
  owner_role: '',
  title: '',
  optional: false,
  active: true,
  sequence: 0,
  steps: [],
};

function toDefaults(template: PreflightTemplate | null): FormValues {
  if (!template) return EMPTY;
  return {
    action: template.action ?? '',
    group: template.group != null ? String(template.group) : '',
    task_type: template.task_type,
    owner_role: template.owner_role ?? '',
    title: template.title ?? '',
    optional: template.optional,
    active: template.active,
    sequence: template.sequence ?? 0,
    steps: template.steps.map((s) => ({ title: s.title ?? '', description: s.description ?? '' })),
  };
}

interface Props {
  open: boolean;
  /** null = create; a record = edit (action is then read-only). */
  template: PreflightTemplate | null;
  onClose: () => void;
}

export function PreflightTemplateDialog({ open, template, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const isEdit = template !== null;

  // Group + action catalog options (only needed while the dialog is open).
  const groupsQuery = useQuery({
    queryKey: ['preflight', 'groups', 'all'],
    queryFn: () => groups.list({ pageSize: 200, ordering: 'sequence' }),
    enabled: open,
    staleTime: 60_000,
  });
  const catalogQuery = useQuery({
    queryKey: ['preflight', 'actions'],
    queryFn: () => getActionCatalog(),
    enabled: open && !isEdit,
    staleTime: 60_000,
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(template),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'steps' });

  useEffect(() => {
    if (open) reset(toDefaults(template));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  // Picking an action from the catalog prefills the registry defaults.
  const applyActionDefaults = (def: PreflightActionDef | null) => {
    setValue('action', def?.action ?? '', { shouldDirty: true, shouldValidate: true });
    if (!def) return;
    setValue('title', def.default_title ?? '', { shouldDirty: true });
    setValue('task_type', def.task_type, { shouldDirty: true });
    setValue('owner_role', def.owner_role ?? '', { shouldDirty: true });
    setValue('optional', def.optional, { shouldDirty: true });
    setValue(
      'steps',
      (def.default_steps ?? []).map((s) => ({ title: s.title ?? '', description: s.description ?? '' })),
      { shouldDirty: true },
    );
    // Prefill the group Select from the action's group_code, if it maps to a
    // known group.
    const g = groupsQuery.data?.results.find((x) => x.code === def.group_code);
    if (g) setValue('group', String(g.id), { shouldDirty: true });
  };

  const mutation = useMutation({
    mutationFn: (body: PreflightTemplateWrite) =>
      isEdit ? templates.update(template.id, body) : templates.create(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Template updated.' : 'Template created.', 'success');
      qc.invalidateQueries({ queryKey: ['preflight', 'templates'] });
      onClose();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    const body: PreflightTemplateWrite = {
      group: v.group ? Number(v.group) : null,
      task_type: v.task_type,
      owner_role: (v.owner_role || '') as PreflightOwnerRole,
      title: v.title.trim(),
      optional: v.optional,
      active: v.active,
      sequence: v.sequence,
      steps: v.steps.map((s) => ({ title: s.title.trim(), description: s.description.trim() })),
    };
    // The action keys the template; only send it on create (immutable on edit).
    if (!isEdit) body.action = v.action.trim();
    mutation.mutate(body);
  });

  const groupOptions = groupsQuery.data?.results ?? [];
  const actionOptions = catalogQuery.data?.actions ?? [];
  const taskTypes = catalogQuery.data?.task_types ?? (['manual', 'async', 'multistep'] as PreflightTaskType[]);
  const ownerRoles = catalogQuery.data?.owner_roles ?? [];
  // Owner-role options: the catalog list minus the blank sentinel (rendered
  // as an explicit "Unassigned" item); fall back to the static label keys.
  const ownerRoleValues = (ownerRoles.length
    ? ownerRoles.filter((r): r is Exclude<PreflightOwnerRole, ''> => r !== '')
    : (Object.keys(OWNER_ROLE_LABELS) as Exclude<PreflightOwnerRole, ''>[]));

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? `Edit template · ${template.action}` : 'New checklist template'}
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </>
      }
    >
      <Box component="form" onSubmit={submit} noValidate sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Action --------------------------------------------------------- */}
        {isEdit ? (
          <TextField
            label="Action"
            size="small"
            fullWidth
            value={template.action}
            disabled
            helperText="The action keys the template and cannot be changed."
          />
        ) : (
          <Controller
            control={control}
            name="action"
            render={({ field }) => (
              <Autocomplete
                options={actionOptions}
                loading={catalogQuery.isLoading}
                getOptionLabel={(o) => o.action}
                isOptionEqualToValue={(o, v) => o.action === v.action}
                value={actionOptions.find((o) => o.action === field.value) ?? null}
                onChange={(_, v) => applyActionDefaults(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.action}>
                    <Box>
                      <Typography variant="body2">{o.action}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.default_title || o.group_code} · {TASK_TYPE_LABELS[o.task_type]}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Action"
                    size="small"
                    error={Boolean(errors.action)}
                    helperText={errors.action?.message ?? 'Picking an action prefills its registry defaults.'}
                  />
                )}
              />
            )}
          />
        )}

        {/* Grouping / classification -------------------------------------- */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={control}
            name="group"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Group"
                size="small"
                fullWidth
                error={Boolean(errors.group)}
                helperText={errors.group?.message}
              >
                <MenuItem value="">
                  <em>Ungrouped</em>
                </MenuItem>
                {groupOptions.map((g) => (
                  <MenuItem key={g.id} value={String(g.id)}>
                    {g.name} ({g.code})
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="task_type"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Task type"
                size="small"
                fullWidth
                error={Boolean(errors.task_type)}
                helperText={errors.task_type?.message}
              >
                {taskTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TASK_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="owner_role"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Owner role"
                size="small"
                fullWidth
                error={Boolean(errors.owner_role)}
                helperText={errors.owner_role?.message}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {ownerRoleValues.map((r) => (
                  <MenuItem key={r} value={r}>
                    {OWNER_ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>

        <TextField
          {...register('title')}
          label="Title"
          size="small"
          fullWidth
          error={Boolean(errors.title)}
          helperText={errors.title?.message}
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <Controller
            control={control}
            name="optional"
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Optional"
              />
            )}
          />
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Active"
              />
            )}
          />
          <TextField
            {...register('sequence', { valueAsNumber: true })}
            label="Sequence"
            size="small"
            type="number"
            sx={{ width: 140 }}
            error={Boolean(errors.sequence)}
            helperText={errors.sequence?.message}
          />
        </Stack>

        {/* Steps editor --------------------------------------------------- */}
        <Divider />
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Steps
          </Typography>
          {fields.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              No steps. Multistep templates render one checklist row per step.
            </Alert>
          )}
          <Stack spacing={1.5}>
            {fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1} alignItems="flex-start">
                <Controller
                  control={control}
                  name={`steps.${i}.title`}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={`Step ${i + 1} title`}
                      size="small"
                      sx={{ width: 240 }}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`steps.${i}.description`}
                  render={({ field }) => (
                    <TextField {...field} label="Description" size="small" fullWidth multiline minRows={1} />
                  )}
                />
                <IconButton size="small" aria-label="Remove step" onClick={() => remove(i)} sx={{ mt: 0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => append({ title: '', description: '' })}
          >
            Add step
          </Button>
        </Box>
      </Box>
    </AppDialog>
  );
}
